import { Link, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ArrowRightIcon,
  MapPinIcon,
  ClockIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  ShoppingCartIcon,
  ArrowPathIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';
import { useRobotsMeta } from '@/hooks/useRobotsMeta';
import { usePageMeta } from '@/hooks/usePageMeta';
import DemoVideo from '@/components/common/DemoVideo';

/** 一文字の判子風スタンプ（デザインアクセント） */
function KanjiStamp({ char, className = '' }: { char: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-[2.5px] border-primary-600/80 text-primary-700 font-bold text-2xl select-none ${className}`}
    >
      {char}
    </span>
  );
}

export default function Home() {
  usePageMeta({
    title: '企画苔 - 簡単企画作成サイト',
    description: '企画苔なら、企画もリンク共有も参加登録も全部かんたん。メンバーは認証なしで参加でき、ゲストリストや参加状況を自動集計。イベント企画の手間をゼロに。',
  });
  useRobotsMeta('index,follow');
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { isLoading: isProfileLoading } = useUser();

  // JSON-LD: WebApplication schema for SEO and LLM
  useEffect(() => {
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: '企画苔（きかくごけ）',
      alternateName: 'Kikakugoke',
      applicationCategory: 'BusinessApplication',
      description:
        'AIがイベント企画を提案し、認証不要でゲストも参加できるイベント管理SaaS。日本語対応、祝日対応、AI生成プランなど。',
      url: 'https://kikakugoke.com',
      logo: 'https://kikakugoke.com/images/logo_03_icon_transparent.png',
      image: 'https://kikakugoke.com/images/logo_03_wide_transparent.png',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'JPY',
        availability: 'https://schema.org/InStock',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Support',
        email: 'support@kikakugoke.com',
        availableLanguage: ['ja'],
      },
      inLanguage: 'ja',
      operatingSystem: 'Web Browser',
      browserRequirements: 'Requires JavaScript. Requires HTML5.',
    });
    document.head.appendChild(schemaScript);

    return () => {
      schemaScript.remove();
    };
  }, []);

  // JSON-LD: WebSite schema
  useEffect(() => {
    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '企画苔（きかくごけ）',
      url: 'https://kikakugoke.com',
      inLanguage: 'ja',
    });
    document.head.appendChild(websiteScript);

    return () => {
      websiteScript.remove();
    };
  }, []);

  // After login (including OAuth callback landing on '/'), take users to the app.
  // If onboarding is incomplete, OnboardingGuard will redirect accordingly.
  // OAuth後はsessionStorageのreturnTo先に戻す（メールリンク等からの導線を維持）
  if (isAuthenticated && !isAuthLoading && !isProfileLoading) {
    let oauthReturnTo: string | null = null;
    try {
      oauthReturnTo = sessionStorage.getItem('oauth_returnTo');
      sessionStorage.removeItem('oauth_returnTo');
    } catch { /* ignore */ }
    const destination = oauthReturnTo && oauthReturnTo.startsWith('/') ? oauthReturnTo : '/dashboard';
    return <Navigate to={destination} replace />;
  }

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      {/* ヒーロー: コピー + 実画面が動くメインビジュアル */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.3em] text-primary-600 mb-6">
              KIKAKUGOKE
            </p>
            <h1 className="font-black text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.35] text-text-900 mb-6">
              人生は、
              <br />
              <span className="text-primary-700">予定を入れた日</span>だけ進む。
            </h1>
            <p className="text-base sm:text-lg text-text-600 leading-relaxed mb-10">
              企画も、日程調整も、当日の段取りも、これひとつ。
              <br className="hidden sm:block" />
              参加する人は<span className="font-bold text-text-800">アカウント登録なし</span>で、URLを開いて答えるだけ。
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/auth?mode=register"
                className="inline-flex items-center justify-center px-8 py-4 btn-primary text-base sm:text-lg font-bold"
              >
                無料で始める
                <ArrowRightIcon className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center text-text-700 hover:text-primary-700 font-bold text-base transition-colors border-b-2 border-text-300 hover:border-primary-500 pb-0.5"
              >
                機能を見る
              </Link>
            </div>
          </div>

          {/* メインビジュアル: 参加登録の一部始終（実画面） */}
          <div className="mt-16">
            <DemoVideo
              src="hero.mp4"
              poster="poster_hero.jpg"
              mobileSrc="hero_m.mp4"
              mobilePoster="poster_hero_m.jpg"
              label="実際の画面: 招待された人が登録なしで候補日に○×で回答し、アンケートに答えるまで"
              className="rounded-2xl ring-1 ring-text-200/80 shadow-2xl shadow-primary-900/10"
            />
            <p className="mt-4 text-sm text-text-500 text-right">
              実際の画面 — 招待された人は、登録なしで○×とアンケートに答えるだけ
            </p>
          </div>
        </div>
      </section>

      {/* 幹事の仕事は3つだけ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-y border-text-100">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-xs font-bold tracking-[0.3em] text-primary-600 mb-4">PROCESS</p>
            <h2 className="font-black text-3xl sm:text-4xl text-text-900">
              幹事の仕事は、3つだけ。
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-x-10 gap-y-14">
            <div className="relative">
              <div className="flex items-baseline gap-4 mb-5">
                <KanjiStamp char="企" />
                <span className="font-bold text-sm text-text-400 tracking-widest">01</span>
              </div>
              <h3 className="font-bold text-xl text-text-900 mb-3">企画する</h3>
              <p className="text-[15px] leading-relaxed text-text-600">
                「何する？」が決まらなくても大丈夫。3つの質問に答えると、AIがタイトルから予算まで企画案をまるごと提案します。
              </p>
            </div>
            <div className="relative">
              <div className="flex items-baseline gap-4 mb-5">
                <KanjiStamp char="招" />
                <span className="font-bold text-sm text-text-400 tracking-widest">02</span>
              </div>
              <h3 className="font-bold text-xl text-text-900 mb-3">招待する</h3>
              <p className="text-[15px] leading-relaxed text-text-600">
                イベントページのURLを送るだけ。受け取った人は登録なしで出欠に○×、アンケートまでその場で答えられます。
              </p>
            </div>
            <div className="relative">
              <div className="flex items-baseline gap-4 mb-5">
                <KanjiStamp char="管" />
                <span className="font-bold text-sm text-text-400 tracking-widest">03</span>
              </div>
              <h3 className="font-bold text-xl text-text-900 mb-3">まとめて管理</h3>
              <p className="text-[15px] leading-relaxed text-text-600">
                集計もリマインドも自動。決まった予定はカレンダーへ、当日の持ち物や割り勘までまとめて管理できます。
              </p>
            </div>
          </div>

          <div className="mt-14">
            <Link
              to="/features"
              className="inline-flex items-center text-primary-700 hover:text-primary-800 font-bold transition-colors"
            >
              すべての機能を見る
              <ArrowRightIcon className="ml-1.5 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 深緑バンド: 核心バリュー */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-primary-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-[1fr_auto] items-center gap-10">
            <div>
              <p className="text-xs font-bold tracking-[0.3em] text-primary-200 mb-6">NO SIGN-UP FOR GUESTS</p>
              <h2 className="font-black text-3xl sm:text-4xl lg:text-[2.75rem] leading-snug text-white mb-6">
                参加する人は、
                <br />
                登録すらいらない。
              </h2>
              <p className="text-primary-100/90 text-base sm:text-lg leading-relaxed max-w-xl">
                「アプリ入れて」とお願いしなくていいから、ママ友にも、職場にも、久しぶりの友人にも送りやすい。
                誘いやすさも、幹事の腕。
              </p>
            </div>
            <img
              src="/images/kokeoyako.png"
              alt="苔親子"
              className="w-36 sm:w-44 justify-self-center md:justify-self-end"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* 便利ツールセクション */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-bold tracking-[0.3em] text-primary-600 mb-4">FREE TOOLS</p>
            <h2 className="font-black text-3xl sm:text-4xl text-text-900 mb-3">
              ツールだけでも、使える。
            </h2>
            <p className="text-text-600">
              登録不要・無料・広告なし。準備から当日・精算まで、単体で使える便利ツールも揃っています。
            </p>
          </div>

          {(() => {
            const tools = [
              { icon: MapPinIcon, name: 'みんなの中間地点', description: '集まりやすい駅を検索', to: '/tools/midpoint-station' },
              { icon: MapIcon, name: 'スポットシェア', description: '候補の場所をまとめて比較', to: '/tools/spot-share' },
              { icon: ClipboardDocumentCheckIcon, name: 'チェックリスト', description: 'リストを共有して各自チェック', to: '/tools/packing-list' },
              { icon: ShoppingCartIcon, name: '買い出しリスト', description: '品目と担当をシェア', to: '/tools/shopping-list' },
              { icon: ClockIcon, name: 'タイムテーブル', description: '当日の進行表を作成・共有', to: '/tools/timetable' },
              { icon: ArrowPathIcon, name: 'ルーレット', description: '役割決め・順番決めに', to: '/tools/random-picker' },
              { icon: BanknotesIcon, name: 'かんたん割り勘', description: '誰がいくら払うか一目瞭然', to: '/tools/warikan' },
            ];
            const renderCard = (tool: typeof tools[number], index: number) => (
              <Link
                key={`${tool.to}-${index}`}
                to={tool.to}
                className="card p-4 sm:p-5 hover:border-primary-200 transition-colors text-center group shrink-0 w-36 sm:w-44"
              >
                <tool.icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary-500 mx-auto mb-2 sm:mb-3 group-hover:text-primary-600 transition-colors" />
                <h3 className="text-sm sm:text-base font-semibold text-text-900 mb-1">{tool.name}</h3>
                <p className="text-xs sm:text-sm text-text-500 leading-snug">{tool.description}</p>
              </Link>
            );
            return (
              <div className="overflow-hidden">
                <div
                  className="home-tools-marquee flex gap-3 sm:gap-5 hover:[animation-play-state:paused]"
                  style={{ width: 'max-content' }}
                >
                  {tools.map((tool, i) => renderCard(tool, i))}
                  {tools.map((tool, i) => renderCard(tool, i + tools.length))}
                </div>
                <style>{`
                  @keyframes scroll-left {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                  }
                  /* モバイルは画面幅に対する相対速度が上がるためゆっくりに */
                  .home-tools-marquee {
                    animation: scroll-left 55s linear infinite;
                  }
                  @media (min-width: 640px) {
                    .home-tools-marquee {
                      animation-duration: 30s;
                    }
                  }
                `}</style>
              </div>
            );
          })()}

          <div className="mt-10">
            <Link
              to="/tools"
              className="inline-flex items-center text-primary-700 hover:text-primary-800 font-bold transition-colors"
            >
              すべてのツールを見る
              <ArrowRightIcon className="ml-1.5 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-primary-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-black text-3xl sm:text-4xl text-white mb-6">
            次の集まりから、
            <br className="sm:hidden" />
            幹事をやめない理由をつくる。
          </h2>
          <p className="text-lg text-primary-100/90 mb-10">無料プランで、すべての基本機能を使えます</p>
          <Link
            to="/auth?mode=register"
            className="inline-flex items-center justify-center px-10 py-4 bg-white text-primary-800 rounded-xl hover:bg-primary-50 transition-colors font-bold text-lg shadow-lg"
          >
            無料で始める
            <ArrowRightIcon className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
